import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaScheduleV2Service, serviceName } from '../service';
import { EntryIO, ServiceQueries } from '../types';
import { useScheduleV2Queries } from './use-schedule-v2-queries';
import { UseScheduleV2QueryEndpointArgs, useScheduleV2Query } from './use-schedule-v2-query';

/**
 * A hook that returns a query for the `Entry` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useEntryQuery = <E = unknown, D = EntryIO['output']>(
  args: UseScheduleV2QueryEndpointArgs<'Entry', E, D>
) =>
  useScheduleV2Query({
    endpointName: 'Entry',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `Entry` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useEntryQueries = <E = unknown, D = EntryIO['output']>(
  argsArr: UseScheduleV2QueryEndpointArgs<'Entry', E, D>[]
) =>
  useScheduleV2Queries(
    argsArr.map((args) => ({
      endpointName: 'Entry',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `Entry` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchEntry = () => {
  const queryClient = useQueryClient();

  const fetchEntry = async <E = unknown, D = EntryIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseScheduleV2QueryEndpointArgs<'Entry', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      EntryIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'Entry'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        EntryIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'Entry'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'Entry'>({
          serviceName,
          endpointName: 'Entry',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaScheduleV2Service.Entry(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchEntry;
};
